<script>
import Vue from "vue";
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import TemplateForm from "./forms/email-template-form.vue";
import AddButton from "@/components/buttons/add-button.vue";
import api from "@/helpers/apirest/api";

// Matches euro26-api config constants.pagination.max_per_page (MAX_PER_PAGE default).
const LIST_PER_PAGE = 500;
const ZOHO_CAMPAIGNS_TYPE = "zoho_campaigns";

/**
 * Email template catalog: type badge, name search and a single type chip.
 */
export default {
  components: {
    Layout,
    PageHeader,
    AddButton,
    TemplateForm,
  },
  data() {
    return {
      modalId:'template-form-modal',      
      autorefreshInterval: 5,
      intervalId: null,
      pageHeader:{
        title: "Plantillas de correo",
        breadcrumbs: [
          {
            text: "Plantillas de correo",
            href: "/email-templates",
            active: true
          }
        ],
      },
      templates: [],
      templateTypes: [],
      selectedTypeId: null,
      nameQuery: "",
      selectedItemId:null, 
      showSelectedItemId:null,
    };
  },
  computed: {
    filteredTemplates() {
      const query = (this.nameQuery || "").trim().toLowerCase();

      return this.templates.filter((template) => {
        if (this.selectedTypeId && template?.type !== this.selectedTypeId) {
          return false;
        }

        if (!query) {
          return true;
        }

        return (template?.name || "").toLowerCase().includes(query);
      });
    },
    showNoMatches() {
      return this.templates.length > 0 && this.filteredTemplates.length === 0;
    },
  },
  methods: {
    showEditModal(item) {
      this.selectedItemId = item?.id || null;
      this.$bvModal.show(this.modalId);
    },
    onSuccess(){
      this.loadData();
    },
    isZohoCampaign(template) {
      return template?.type === ZOHO_CAMPAIGNS_TYPE;
    },
    typeLabel(typeId) {
      const match = this.templateTypes.find((item) => item.id === typeId);
      return match?.label || typeId || "";
    },
    selectType(typeId) {
      if (typeId === null || this.selectedTypeId === typeId) {
        this.selectedTypeId = null;
        return;
      }

      this.selectedTypeId = typeId;
    },
    async loadTemplateTypes() {
      try {
        const response = await api.get({
          url: "email-templates/types",
        });

        this.templateTypes = response?.data?.data || [];
      } catch (error) {
        this.templateTypes = [];
      }
    },
    async loadData() {
        try {            
            const response = await api.get({
                url:'email-templates',
                params:{
                  with_trashed: 1,
                  per_page: LIST_PER_PAGE,
                },
                config: {
                    withLoading: true
                }
            });

            const templates = response?.data?.data;

            templates.map(item => {
                item.soft_delete_status = (item?.deleted_at) ? 0 : 1;
            });

            this.templates = templates;
        } catch (error) {
          this.templates = [];
        }
    },
    async deleteTemplate(template){
        const result = await Vue.swal({
          title: "Eliminar plantilla",
          icon: "info",
          html:  `Está seguro que desea eliminar la plantilla: ${template?.name}?<br>La accion no se puede deshacer!`,
          showCloseButton: false,
          showCancelButton: true,
          focusConfirm: false,
          confirmButtonText: 'Sí',
          cancelButtonText: 'Cancelar',
        });

        if (result.value) {
          await api.delete({
              url: 'email-templates',
              id: template?.id,
              force: true
          });

          this.loadData();
        }        
    },
    async softDeleteRow(item){
        if(!item?.deleted_at){
            await api.delete({
                url: 'email-templates',
                id: item?.id
            });
        }else{
            await api.post({
                url:`email-templates/${item?.id}/restore`
            });
        }

        this.loadData();
    },
    async sendTestEmail(template) {
        const { value: email } = await Vue.swal({
            title: 'Enviar correo de prueba',
            input: 'email',
            inputLabel: 'Correo electrónico',
            inputPlaceholder: 'Ingrese el correo electrónico de prueba',
            showCancelButton: true,
            confirmButtonText: 'Enviar',
            cancelButtonText: 'Cancelar',
            inputValidator: (value) => {
                if (!value) {
                    return 'Debe ingresar un correo electrónico';
                }
            }
        });

        if (email) {
            try {
                Vue.swal({
                    title: 'Enviando correo de prueba...',
                    text: 'Por favor espere mientras se envía el correo',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    allowEnterKey: false,
                    showConfirmButton: false,
                    showCancelButton: false,
                    didOpen: () => {
                        Vue.swal.showLoading();
                    }
                });

                await api.post({
                    url: `email-templates/${template.id}/send-test`,
                    data: { email }
                });

                Vue.swal({
                    title: 'Éxito',
                    text: 'El correo de prueba ha sido enviado correctamente',
                    icon: 'success'
                });
            } catch (error) {
                Vue.swal({
                    title: 'Error',
                    text: 'Ha ocurrido un error al enviar el correo de prueba',
                    icon: 'error'
                });
            }
        }
    },
    async copyPublicUrl(template) {
        const url = template?.public_url;
        if (!url) {
            return;
        }

        try {
            await navigator.clipboard.writeText(url);
            Vue.swal({
                title: 'Enlace copiado',
                text: 'La URL de la plantilla se copió al portapapeles',
                icon: 'success'
            });
        } catch (error) {
            Vue.swal({
                title: 'No se pudo copiar',
                text: url,
                icon: 'info'
            });
        }
    },
  },
  mounted() {
    this.loadData();
    this.loadTemplateTypes();
  },
  beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
};
</script>

<template>
  <Layout>
    <PageHeader :title="pageHeader?.title" :items="pageHeader?.breadcrumbs" />
    <TemplateForm :modal-id="modalId" :id="selectedItemId" @onSuccess="onSuccess"></TemplateForm>    
    <div class="row">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <p class="text-muted font-13 mb-0">Administra tus plantillas de correo para los diferentes procesos.</p>
              <div class="text-right">
                <AddButton @click="showEditModal"></AddButton>
              </div>
            </div>
            <div class="mt-3">
              <b-form-input
                v-model="nameQuery"
                type="search"
                placeholder="Buscar por nombre"
                autocomplete="off"
              ></b-form-input>
              <div class="d-flex flex-wrap mt-3">
                <b-button
                  size="sm"
                  class="mr-2 mb-2"
                  :variant="selectedTypeId === null ? 'primary' : 'light'"
                  @click="selectType(null)"
                >Todas</b-button>
                <b-button
                  v-for="type in templateTypes"
                  :key="type.id"
                  size="sm"
                  class="mr-2 mb-2"
                  :variant="selectedTypeId === type.id ? 'primary' : 'light'"
                  @click="selectType(type.id)"
                >{{ type.label }}</b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <b-skeleton-wrapper :loading="$store.state.loading.loading" class="col-12">
          <template #loading>
            <div class="row">
              <div class="col-xl-3 col-sm-6" v-for="n in 4" :key="n"> 
                <!-- Simulación de 4 tarjetas -->
                <div class="card h-100">
                  <div class="card-body">
                    <div class="d-flex align-items-center justify-content-end">
                      <!-- Checkbox -->
                      <b-skeleton width="20px" height="20px" class="mr-2"></b-skeleton>
                      <!-- Dropdown -->
                      <b-skeleton width="25px" height="25px"></b-skeleton>
                    </div>
                    <!-- Avatar Placeholder -->
                    <div class="text-center">
                      <div class="d-flex justify-content-center">
                        <b-skeleton width="50px" height="50px" class="rounded-circle"></b-skeleton>
                      </div>
                    </div>
                    <!-- Nombre del template -->
                    <div class="media-body text-center mt-3 d-flex align-items-center justify-content-center">
                      <b-skeleton width="60%"></b-skeleton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <div v-if="showNoMatches" class="col-12">
            <div class="card">
              <div class="card-body text-center text-muted">
                Ninguna plantilla coincide
              </div>
            </div>
          </div>
          <div class="col-xl-3 col-sm-6 mb-3" v-for="template in filteredTemplates" :key="template.id">
            <div class="card h-100" :class="{'opacity-5': template?.deleted_at}">
              <div class="card-body">
                <div class="d-flex align-items-center justify-content-end">
                  <b-form-checkbox
                      v-model="template.soft_delete_status"
                      switch class="mb-1"
                      value="1"
                      @change="softDeleteRow(template)"
                  ></b-form-checkbox>
                  <b-dropdown right class="qlk-dropdown-sm" variant="white" size="sm">
                    <template v-slot:button-content>
                      <i class="mdi mdi-dots-vertical m-0 text-muted font-size-20"></i>
                    </template>
                    <b-dropdown-item @click="showEditModal(template)">Editar</b-dropdown-item>
                    <b-dropdown-item v-if="template.public_url" @click="copyPublicUrl(template)">Copiar enlace</b-dropdown-item>
                    <b-dropdown-item v-if="template.type !== 'zoho_campaigns'" @click="sendTestEmail(template)">Enviar correo de prueba</b-dropdown-item>
                    <b-dropdown-item @click="deleteTemplate(template)">Eliminar</b-dropdown-item>
                  </b-dropdown>
                </div>
                <div class="text-center">
                  <div class="text-center d-flex justify-content-center">
                    <div class="avatar-sm mt-2 mb-3 text-center">
                      <span
                        class="avatar-title text-center rounded-circle"
                        :class="isZohoCampaign(template) ? 'bg-soft-info text-info' : 'bg-soft-primary text-primary'"
                      >
                        <i :class="isZohoCampaign(template) ? 'mdi mdi-bullhorn-outline font-size-20' : 'mdi mdi-email-outline font-size-20'"></i>
                      </span>
                    </div>
                  </div>
                  <div class="media-body">
                    <h5 class="template-name mb-2" :title="template?.name">
                      <a href="#" class="text-dark">{{ template?.name }}</a>
                    </h5>
                    <span
                      class="badge template-type-badge"
                      :class="isZohoCampaign(template) ? 'badge-soft-info' : 'badge-soft-primary'"
                    >{{ typeLabel(template?.type) }}</span>
                    <p
                      v-if="isZohoCampaign(template) && template.public_url"
                      class="text-muted font-13 mb-0 mt-2"
                    >Enlace público</p>
                    <p
                      v-else-if="!isZohoCampaign(template) && template.subject"
                      class="text-muted font-13 mb-0 mt-2 text-truncate"
                      :title="template.subject"
                    >{{ template.subject }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </b-skeleton-wrapper>
    </div>
  </Layout>
</template>

<style scoped>
.template-name {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
}

.template-type-badge {
  white-space: normal;
}
</style>
